// Backend API service for SiriDharini contact forms
import { API_BASE_URL } from '../config/api';

// Debug logging
console.log('🔧 SiriDharini Backend Service Debug:');
console.log('   Using API_BASE_URL:', API_BASE_URL);

/**
 * Submit contact form to backend API
 * @param {Object} formData - Form data from contact form
 * @param {string} formData.name - User's name
 * @param {string} formData.email - User's email
 * @param {string} formData.phone - User's phone number
 * @param {string} formData.address - User's address (optional)
 * @param {string} formData.message - User's message
 * @returns {Promise} - API response
 */
export const submitContactForm = async (formData) => {
  try {
    // Validate required fields
    if (!formData.name || !formData.email || !formData.message) {
      throw new Error('Name, email, and message are required fields');
    }

    // Prepare payload for backend
    const payload = {
      name: formData.name.trim(),
      email: formData.email.toLowerCase().trim(),
      phone: formData.phone?.trim() || null,
      address: formData.address?.trim() || null,
      subject: `New Inquiry from ${formData.name} - SiriDharini Farmlands`,
      message: formData.message.trim(),
      queryType: 'GENERAL',
      priority: 'MEDIUM',
      websiteSource: 'SIRIDHARINI'
    };

    // Send request to backend
    const apiUrl = `${API_BASE_URL}/contact/submit`;
    console.log('🚀 SiriDharini API Call:', apiUrl);
    
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    });

    const result = await response.json();

    if (response.ok && result.success) {
      return {
        success: true,
        message: result.message || 'Thank you for your inquiry. We\'ll get back to you soon!',
        data: result.data
      };
    } else {
      throw new Error(result.error || 'Failed to send message. Please try again.');
    }

  } catch (error) {
    
    // Return user-friendly error message
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      return {
        success: false,
        message: 'Unable to connect to server. Please check your internet connection and try again.',
        error: error.message
      };
    }
    
    return {
      success: false,
      message: error.message || 'Failed to send message. Please try again later.',
      error: error.message
    };
  }
};

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {boolean} - Valid email format
 */
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate phone number (Indian format)
 * @param {string} phone - Phone number to validate
 * @returns {boolean} - Valid phone format
 */
export const validatePhone = (phone) => {
  const phoneRegex = /^[\+]?[1-9]?[\d\s\-\(\)]{10,15}$/;
  return phoneRegex.test(phone);
};

/**
 * Sanitize form data to prevent XSS
 * @param {string} input - Input to sanitize
 * @returns {string} - Sanitized input
 */
export const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;
  return input
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');
};

/**
 * Test backend connection
 * @returns {Promise} - Connection test result
 */
export const testBackendConnection = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/contact/health`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    const result = await response.json();
    
    return {
      success: response.ok,
      message: result.message || 'Backend connection test',
      data: result.data
    };
  } catch (error) {
    console.error('Backend connection test failed:', error);
    return {
      success: false,
      message: 'Failed to connect to backend',
      error: error.message
    };
  }
};

export default {
  submitContactForm,
  validateEmail,
  validatePhone,
  sanitizeInput,
  testBackendConnection
};